/**
 * 培训标准管理页面
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Alert,
  Row,
  Col,
  Statistic,
  Tag,
  Switch,
  Upload,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExportOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { 
  TrainingStandard, 
  TrainingStandardFormData, 
  TrainingBusiness, 
  TrainingElement,
  PaginationParams 
} from '../../types';
import { 
  getTrainingStandards, 
  createTrainingStandard, 
  updateTrainingStandard, 
  deleteTrainingStandard,
  getTrainingStandardStats,
  getTrainingBusinesses,
  getTrainingElements,
  downloadTrainingStandardTemplate,
  deleteAllTrainingStandards,
  exportTrainingStandards
} from '../../services/api';
import { formatDateTime, getCityLevelLabel } from '../../utils/helpers';
import { CITY_LEVEL_OPTIONS } from '../../utils/constants';
import { parseTrainingStandardExcel } from '../../utils/excelTemplate';
import { exportTrainingStandardsToExcel, showConfirmDialog } from '../../utils/exportUtils';

const { Option } = Select;
const { TextArea } = Input;

const TrainingStandards: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [standards, setStandards] = useState<TrainingStandard[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<number | undefined>();
  const [selectedElementId, setSelectedElementId] = useState<number | undefined>();
  const [selectedCityLevel, setSelectedCityLevel] = useState<number | undefined>();
  
  // 统计数据状态
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStandard, setEditingStandard] = useState<TrainingStandard | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  
  // 批量设置城市等级状态
  const [batchCityMode, setBatchCityMode] = useState(false);
  const [cityRanges, setCityRanges] = useState<{[key: number]: {min_range?: number; max_range?: number}}>({});
  
  // 选择数据
  const [businesses, setBusinesses] = useState<TrainingBusiness[]>([]);
  const [elements, setElements] = useState<TrainingElement[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [elementsLoading, setElementsLoading] = useState(false);
  
  // Excel导入状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadStandards();
    loadStats();
    loadBusinesses();
    loadElements();
  }, [currentPage, pageSize, searchText, selectedBusinessId, selectedElementId, selectedCityLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载培训标准列表
  const loadStandards = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
        businessId: selectedBusinessId,
        elementId: selectedElementId,
        level: selectedCityLevel,
      };
      
      const data = await getTrainingStandards(params);
      setStandards(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('加载培训标准列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载培训标准列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getTrainingStandardStats();
      setTotalCount(data.total);
      setActiveCount(data.active);
    } catch (err) {
      console.error('加载统计数据失败:', err);
      message.error(err instanceof Error ? err.message : '加载统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  };

  // 加载培训业务列表
  const loadBusinesses = async () => {
    try {
      setBusinessesLoading(true);
      const data = await getTrainingBusinesses({ page: 1, limit: 1000 });
      setBusinesses(data.data);
    } catch (err) {
      console.error('加载培训业务列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载培训业务列表失败');
    } finally {
      setBusinessesLoading(false);
    }
  };

  // 加载培训要素列表
  const loadElements = async () => {
    try {
      setElementsLoading(true);
      const data = await getTrainingElements({ page: 1, limit: 1000 });
      setElements(data.data);
    } catch (err) {
      console.error('加载培训要素列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载培训要素列表失败');
    } finally {
      setElementsLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // 处理业务筛选
  const handleBusinessFilter = (value: number | undefined) => {
    setSelectedBusinessId(value);
    setCurrentPage(1);
  };

  // 处理要素筛选
  const handleElementFilter = (value: number | undefined) => {
    setSelectedElementId(value);
    setCurrentPage(1);
  };

  // 处理城市等级筛选
  const handleCityLevelFilter = (value: number | undefined) => {
    setSelectedCityLevel(value);
    setCurrentPage(1);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingStandard(null);
    setModalVisible(true);
    setBatchCityMode(false);
    setCityRanges({});
    form.resetFields();
  };

  // 打开编辑模态框
  const handleEdit = (standard: TrainingStandard) => {
    setEditingStandard(standard);
    setModalVisible(true);
    setBatchCityMode(false);
    setCityRanges({});
    form.setFieldsValue({
      business_id: standard.business_id,
      element_id: standard.element_id,
      city_level: standard.city_level,
      min_range: standard.min_range,
      max_range: standard.max_range,
      unit: standard.unit,
      remarks: standard.remarks,
      is_active: standard.is_active === 1,
    });
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 自定义验证：检查最小值和最大值的关系
      if (values.min_range !== undefined && values.max_range !== undefined) {
        if (values.min_range > values.max_range) {
          message.error('最小值不能大于最大值');
          return;
        }
      }
      
      setSubmitting(true);

      if (editingStandard) {
        // 编辑培训标准 (不支持批量)
        const formData: TrainingStandardFormData = {
          business_id: values.business_id,
          element_id: values.element_id,
          city_level: values.city_level,
          min_range: values.min_range,
          max_range: values.max_range,
          unit: values.unit.trim(),
          remarks: values.remarks?.trim() || '',
          is_active: values.is_active ? 1 : 0,
        };
        
        await updateTrainingStandard(editingStandard.id, formData);
        message.success('编辑培训标准成功');
      } else {
        // 新增培训标准
        if (batchCityMode) {
          // 批量创建模式：为所有城市等级创建标准
          
          // 验证批量设置中的最小值和最大值关系
          const invalidRanges: number[] = [];
          for (const cityLevel of [1, 2, 3, 4, 5]) {
            const cityRange = cityRanges[cityLevel];
            if (cityRange && cityRange.min_range !== undefined && cityRange.max_range !== undefined) {
              if (cityRange.min_range > cityRange.max_range) {
                invalidRanges.push(cityLevel);
              }
            }
          }
          
          if (invalidRanges.length > 0) {
            message.error(`${invalidRanges.map(level => `${level}级`).join('、')}城市的最小值不能大于最大值`);
            return;
          }
          
          let successCount = 0;
          let failedCities: number[] = [];
          
          for (const cityLevel of [1, 2, 3, 4, 5]) {
            const cityRange = cityRanges[cityLevel];
            if (cityRange && cityRange.min_range !== undefined && cityRange.max_range !== undefined) {
              try {
                const formData: TrainingStandardFormData = {
                  business_id: values.business_id,
                  element_id: values.element_id,
                  city_level: cityLevel,
                  min_range: cityRange.min_range,
                  max_range: cityRange.max_range,
                  unit: values.unit.trim(),
                  remarks: values.remarks?.trim() || '',
                  is_active: values.is_active ? 1 : 0,
                };
                
                await createTrainingStandard(formData);
                successCount++;
              } catch (err) {
                console.error(`创建${cityLevel}级城市标准失败:`, err);
                failedCities.push(cityLevel);
              }
            }
          }
          
          if (successCount > 0) {
            message.success(`成功创建 ${successCount} 个城市等级的培训标准`);
          }
          if (failedCities.length > 0) {
            message.warning(`${failedCities.map(level => `${level}级`).join('、')}城市标准创建失败`);
          }
        } else {
          // 单个创建模式
          const formData: TrainingStandardFormData = {
            business_id: values.business_id,
            element_id: values.element_id,
            city_level: values.city_level,
            min_range: values.min_range,
            max_range: values.max_range,
            unit: values.unit.trim(),
            remarks: values.remarks?.trim() || '',
            is_active: values.is_active ? 1 : 0,
          };
          
          await createTrainingStandard(formData);
          message.success('新增培训标准成功');
        }
      }
      
      setModalVisible(false);
      loadStandards();
      loadStats();
    } catch (err) {
      console.error('提交失败:', err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 删除培训标准
  const handleDelete = async (standard: TrainingStandard) => {
    try {
      await deleteTrainingStandard(standard.id);
      message.success('删除培训标准成功');
      loadStandards();
      loadStats();
    } catch (err) {
      console.error('删除失败:', err);
      message.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  // 下载模板
  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTrainingStandardTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '培训标准导入模板.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error('下载模板失败');
    }
  };

  // Excel导入状态变化处理（简化版本，主要逻辑在customUpload中）
  const handleImport = (info: any) => {
    const { file } = info;
    
    if (file.status === 'uploading') {
      setUploading(true);
    } else if (file.status === 'error') {
      setUploading(false);
    }
    // 成功和错误处理都在customUpload中完成
  };

  // 自定义上传
  const customUpload = async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    
    console.log('开始上传文件:', file.name, file.type, file.size);
    
    try {
      setUploading(true);
      onProgress({ percent: 10 });
      
      // 1. 获取业务和要素映射
      console.log('当前业务数据:', businesses.length, businesses);
      console.log('当前要素数据:', elements.length, elements);
      
      const businessMapping = new Map<string, number>();
      businesses.forEach(business => {
        businessMapping.set(business.name, business.id);
      });

      const elementMapping = new Map<string, number>();
      elements.forEach(element => {
        elementMapping.set(element.name, element.id);
      });

      onProgress({ percent: 20 });
      
      // 2. 解析Excel文件
      console.log('开始解析Excel文件...');
      const parseResult = await parseTrainingStandardExcel(file, businessMapping, elementMapping);
      console.log('Excel解析结果:', parseResult);
      
      if (!parseResult.success || parseResult.data.length === 0) {
        console.error('Excel解析失败:', parseResult.errors);
        onError(new Error(parseResult.errors.join(', ') || '解析失败'));
        message.error(`Excel解析失败：${parseResult.errors.join(', ')}`);
        return;
      }

      console.log('解析成功，准备插入', parseResult.data.length, '条记录');

      onProgress({ percent: 40 });
      
      // 3. 批量创建培训标准
      let successCount = 0;
      const errors: string[] = [];
      const totalRecords = parseResult.data.length;
      
      for (let i = 0; i < parseResult.data.length; i++) {
        const standardData = parseResult.data[i];
        console.log(`正在创建第${i + 1}条记录:`, standardData);
        
        try {
          const result = await createTrainingStandard(standardData);
          console.log(`第${i + 1}条记录创建成功:`, result);
          successCount++;
          
          // 更新进度
          const progress = 40 + Math.floor((i + 1) / totalRecords * 50);
          onProgress({ percent: progress });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '创建失败';
          console.error(`第${i + 1}条记录创建失败:`, error);
          errors.push(`第${i + 6}行: ${errorMessage}`);
        }
      }

      onProgress({ percent: 100 });
      
      // 4. 显示结果
      if (successCount > 0) {
        onSuccess({ successCount, errors });
        message.success(`导入成功：共${successCount}条记录${totalRecords > successCount ? `，失败${totalRecords - successCount}条` : ''}`);
        
        if (parseResult.warnings && parseResult.warnings.length > 0) {
          message.warning(`注意：${parseResult.warnings.join(', ')}`);
        }
        
        if (errors.length > 0 && errors.length < 5) {
          // 如果错误不多，显示具体错误
          message.error(`部分记录导入失败：${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`);
        }
        
        setImportModalVisible(false);
        loadStandards();
        loadStats();
      } else {
        const allErrors = [...parseResult.errors, ...errors];
        onError(new Error(allErrors.join(', ') || '导入失败'));
        message.error(`导入失败：${allErrors.slice(0, 3).join('; ')}${allErrors.length > 3 ? '...' : ''}`);
      }
      
    } catch (error) {
      onError(error);
      message.error(error instanceof Error ? error.message : '导入失败');
    } finally {
      setUploading(false);
    }
  };

  // 删除全部培训标准
  const handleDeleteAll = async () => {
    try {
      const confirmed = await showConfirmDialog(
        '确定要删除所有培训标准数据吗？\n此操作不可恢复，且如果有检查数据依赖这些标准，将无法删除。'
      );
      
      if (!confirmed) {
        return;
      }

      const result = await deleteAllTrainingStandards();
      message.success(`已删除 ${result.deleted} 条培训标准记录`);
      loadStandards();
      loadStats();
    } catch (err) {
      console.error('删除全部培训标准失败:', err);
      message.error(err instanceof Error ? err.message : '删除全部培训标准失败');
    }
  };

  // 导出培训标准数据
  const handleExportAll = async () => {
    try {
      const allStandards = await exportTrainingStandards();
      exportTrainingStandardsToExcel(allStandards, `培训标准数据_${new Date().toLocaleDateString()}.xlsx`);
      message.success('培训标准数据导出成功');
    } catch (err) {
      console.error('导出培训标准数据失败:', err);
      message.error(err instanceof Error ? err.message : '导出培训标准数据失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<TrainingStandard> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '培训业务',
      dataIndex: 'business_name',
      key: 'business_name',
      width: 150,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '培训要素',
      dataIndex: 'element_name',
      key: 'element_name',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '城市等级',
      dataIndex: 'city_level',
      key: 'city_level',
      width: 100,
      render: (level: number | null) => {
        if (!level) return <Tag color="default">通用</Tag>;
        const colors = ['', 'red', 'orange', 'blue', 'green', 'purple'];
        return (
          <Tag color={colors[level]}>
            {getCityLevelLabel(level)}
          </Tag>
        );
      },
    },
    {
      title: '参考范围',
      key: 'range',
      width: 120,
      render: (_, record: TrainingStandard) => {
        if (record.min_range && record.max_range) {
          return `${record.min_range} - ${record.max_range} ${record.unit}`;
        }
        return '-';
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: number) => (
        <Tag color={isActive === 1 ? 'success' : 'default'}>
          {isActive === 1 ? '启用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatDateTime(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: TrainingStandard) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个培训标准吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="培训标准总数"
              value={totalCount}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="启用标准数"
              value={activeCount}
              loading={statsLoading}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增培训标准
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              下载模板
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              批量导入
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadStandards();
                loadStats();
              }}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportAll}
            >
              导出数据
            </Button>
            <Popconfirm
              title="删除确认"
              description="确定要删除所有培训标准数据吗？此操作不可恢复！"
              onConfirm={handleDeleteAll}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<ClearOutlined />}
              >
                删除全部
              </Button>
            </Popconfirm>
            <Select
              placeholder="筛选培训业务"
              allowClear
              style={{ width: 200 }}
              value={selectedBusinessId}
              onChange={handleBusinessFilter}
              loading={businessesLoading}
            >
              {businesses.map(business => (
                <Option key={business.id} value={business.id}>
                  {business.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="筛选培训要素"
              allowClear
              style={{ width: 150 }}
              value={selectedElementId}
              onChange={handleElementFilter}
              loading={elementsLoading}
            >
              {elements.map(element => (
                <Option key={element.id} value={element.id}>
                  {element.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="筛选城市等级"
              allowClear
              style={{ width: 150 }}
              value={selectedCityLevel}
              onChange={handleCityLevelFilter}
            >
              {CITY_LEVEL_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            <Input.Search
              placeholder="搜索备注..."
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
          </Space>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={standards}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingStandard ? '编辑培训标准' : '新增培训标准'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            is_active: true,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="business_id"
                label="培训业务"
                rules={[{ required: true, message: '请选择培训业务' }]}
              >
                <Select 
                  placeholder="请选择培训业务"
                  loading={businessesLoading}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {businesses.map(business => (
                    <Option key={business.id} value={business.id}>
                      {business.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="element_id"
                label="培训要素"
                rules={[{ required: true, message: '请选择培训要素' }]}
              >
                <Select 
                  placeholder="请选择培训要素"
                  loading={elementsLoading}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {elements.map(element => (
                    <Option key={element.id} value={element.id}>
                      {element.name} ({element.unit})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>


          <Row gutter={16}>
            {!editingStandard && (
              <Col span={24}>
                <Form.Item label="设置模式">
                  <Switch
                    checked={batchCityMode}
                    onChange={(checked) => {
                      setBatchCityMode(checked);
                      if (checked) {
                        // 切换到批量模式时，清空单个城市等级选择
                        form.setFieldsValue({ city_level: undefined });
                        setCityRanges({});
                      }
                    }}
                    checkedChildren="批量设置1-5级城市"
                    unCheckedChildren="单个设置"
                  />
                </Form.Item>
              </Col>
            )}
            
            {!batchCityMode && (
              <Col span={12}>
                <Form.Item
                  name="city_level"
                  label="适用城市等级"
                  tooltip="留空表示通用标准，适用于所有城市等级"
                >
                  <Select placeholder="选择城市等级（可选）" allowClear>
                    {CITY_LEVEL_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={12}>
              <Form.Item
                name="unit"
                label="计量单位"
                rules={[
                  { required: true, message: '请输入计量单位' },
                  { max: 20, message: '计量单位不能超过20个字符' },
                ]}
              >
                <Input placeholder="如：课时、天、人次等" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="启用状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="停用" />
              </Form.Item>
            </Col>
          </Row>

          {!batchCityMode ? (
            // 单个设置模式
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="min_range"
                  label="最小允许值"
                  rules={[
                    { required: true, message: '请输入最小允许值' },
                    { type: 'number', min: 0, message: '最小允许值不能小于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="费用最小允许值"
                    precision={2}
                    min={0}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="max_range"
                  label="最大允许值"
                  rules={[
                    { required: true, message: '请输入最大允许值' },
                    { type: 'number', min: 0, message: '最大允许值不能小于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="费用最大允许值"
                    precision={2}
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            // 批量设置模式 - 1-5级城市
            <div>
              <Form.Item label="城市等级费用范围设置">
                {CITY_LEVEL_OPTIONS.map(option => (
                  <Card key={option.value} size="small" style={{ marginBottom: 8 }}>
                    <Row gutter={8} align="middle">
                      <Col span={6}>
                        <Tag color={['', 'red', 'orange', 'blue', 'green', 'purple'][option.value]}>
                          {option.label}
                        </Tag>
                      </Col>
                      <Col span={8}>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="最小值"
                          precision={2}
                          min={0}
                          value={cityRanges[option.value]?.min_range}
                          onChange={(value) => {
                            setCityRanges(prev => ({
                              ...prev,
                              [option.value]: {
                                ...prev[option.value],
                                min_range: value || undefined
                              }
                            }));
                          }}
                        />
                      </Col>
                      <Col span={2} style={{ textAlign: 'center' }}>
                        至
                      </Col>
                      <Col span={8}>
                        <InputNumber
                          style={{ width: '100%' }}
                          placeholder="最大值"
                          precision={2}
                          min={0}
                          value={cityRanges[option.value]?.max_range}
                          onChange={(value) => {
                            setCityRanges(prev => ({
                              ...prev,
                              [option.value]: {
                                ...prev[option.value],
                                max_range: value || undefined
                              }
                            }));
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Form.Item>
            </div>
          )}

          <Form.Item
            name="remarks"
            label="备注说明"
            rules={[
              { max: 500, message: '备注不能超过500个字符' },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="请输入备注说明（可选）"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <Alert
          message="操作说明"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>培训业务：</strong>选择要设置标准的培训业务类型</li>
              <li><strong>培训要素：</strong>选择要设置标准的费用要素项目</li>
              <li><strong>公司名称：</strong>可选项，为特定公司设置专用标准</li>
              <li><strong>负责人：</strong>可选项，为特定负责人设置专用标准</li>
              <li><strong>设置模式：</strong>
                <ul style={{ marginTop: 4 }}>
                  <li><strong>单个设置：</strong>为单个城市等级或通用情况设置标准</li>
                  <li><strong>批量设置1-5级城市：</strong>一次性为所有城市等级设置不同的费用范围</li>
                </ul>
              </li>
              <li><strong>费用范围：</strong>设置该要素费用的合理范围（最小值-最大值），最小值不能大于最大值，可以相等</li>
              <li><strong>批量设置说明：</strong>
                <ul style={{ marginTop: 4 }}>
                  <li>选择批量模式后，可为1-5级城市分别设置不同的费用范围</li>
                  <li>只会为填写了完整范围（最小值和最大值）的城市等级创建标准</li>
                  <li>可以选择性地只为部分城市等级设置标准</li>
                </ul>
              </li>
              <li><strong>预警逻辑：</strong>实际费用在范围内为正常，超出范围为预警/危险</li>
              <li><strong>通用标准：</strong>公司名称、负责人、城市等级都留空时为通用标准，适用于所有情况</li>
              <li><strong>专用标准：</strong>可为特定的公司、负责人或城市等级设置专门的标准</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>

      {/* Excel导入模态框 */}
      <Modal
        title="批量导入培训标准"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <div>
          <Alert
            message="导入说明"
            description={
              <div>
                <p><strong>步骤：</strong></p>
                <ol>
                  <li>点击"下载模板"获取Excel模板文件</li>
                  <li>按照模板格式填写培训标准数据</li>
                  <li>上传填写完成的Excel文件</li>
                </ol>
                <p><strong>注意事项：</strong></p>
                <ul>
                  <li>培训业务名称和培训要素名称必须事先在系统中存在</li>
                  <li>最小值不能大于最大值（可以相等）</li>
                  <li>相同业务+要素+城市等级的组合不能重复</li>
                  <li>城市等级留空时，系统会自动为1-5级城市各创建一条记录</li>
                  <li>支持.xlsx和.xls格式，文件大小不能超过10MB</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              style={{ marginRight: 16 }}
            >
              下载模板
            </Button>
            
            <Upload.Dragger
              name="file"
              multiple={false}
              accept=".xlsx,.xls"
              customRequest={customUpload}
              onChange={handleImport}
              showUploadList={false}
              style={{ marginTop: 16 }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽Excel文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持.xlsx和.xls格式，文件大小不超过10MB
              </p>
            </Upload.Dragger>
            
            {uploading && (
              <div style={{ marginTop: 16 }}>
                <Spin /> 正在导入数据，请稍候...
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TrainingStandards;